/**
 * MONSTER TOWN — GO THERE, THEN DO IT. FISHING/BUGS CREW (src/catching/**).
 *
 * "Tap a thing -> walk to it, THEN use it" is the game's one play-mode verb
 * (README, Controls). A bug in the grass and a float on the water are not
 * placeables, so the router's auto-walk-then-interact does not cover them, and
 * this is the two-line version for the things this crew owns.
 *
 * WHY IT IMPORTS THE WORLD CREW'S NAV. `navGoTo`/`claimTap` are the town's
 * public walking API — the router, the door hotspots and the signposts all
 * route through them, and a second pathfinder living in this folder would be a
 * second set of bugs about walls. The one thing this crew adds is the WATCHDOG
 * below.
 *
 * THE WATCHDOG IS LAW #1, NOT A NICETY. `navGoTo` drops its arrive callback if
 * it cannot find a route at all (fenced in, a wall built across the shore). A
 * kid who taps a butterfly and gets nothing has tapped a dead thing, so after
 * `GIVE_UP_S` the action runs anyway, wherever the monster ended up. Late is
 * fine; nothing happening is not.
 */

import { engine } from '../core/engine'
import { app } from '../app/context'
import type { Vec3 } from '../core/types'
import { claimTap, navGoTo } from '../world/nav'

/** Close enough that walking would look silly. */
const ALREADY_THERE = 2.4
/** After this the action happens regardless. Generous; never a failure. */
const GIVE_UP_S = 5

interface Pending {
  at: Vec3
  act: () => void
  left: number
  done: boolean
}

const pending: Pending[] = []

/** Claim the tap being dispatched, walk over, and run `act` on arrival. */
export function goThen(target: Vec3, act: () => void): void {
  claimTap()
  const p = app.playerPos
  if (Math.hypot(p.x - target.x, p.z - target.z) <= ALREADY_THERE) {
    act()
    return
  }
  const entry: Pending = { at: target, act, left: GIVE_UP_S, done: false }
  pending.push(entry)
  navGoTo(target, () => {
    if (entry.done) return
    entry.done = true
    act()
  })
}

/**
 * This tap is spoken for and the monster should stay where it is. Used by the
 * catch tap: a kid landing a fish must not also be sent walking to wherever
 * their finger happened to land on the bank.
 */
export function keepStill(): void {
  claimTap()
}

/** Nothing is waiting on a walk any more (scene change, a catch was cancelled). */
export function cancelWalks(): void {
  pending.length = 0
}

export function pendingWalks(): number {
  return pending.filter((p) => !p.done).length
}

export function installWalkWatchdog(): () => void {
  return engine.addSystem({
    id: 'catching.walk',
    order: 47,
    fixed: (dt) => {
      for (let i = pending.length - 1; i >= 0; i--) {
        const p = pending[i]
        if (p === undefined) continue
        if (p.done) {
          pending.splice(i, 1)
          continue
        }
        // Arriving under our own steam counts too — the walker can settle a hair
        // outside nav's arrive radius (see the stall watchdog in world/nav.ts).
        const at = app.playerPos
        if (Math.hypot(at.x - p.at.x, at.z - p.at.z) <= ALREADY_THERE) {
          p.done = true
          pending.splice(i, 1)
          p.act()
          continue
        }
        p.left -= dt
        if (p.left <= 0) {
          p.done = true
          pending.splice(i, 1)
          p.act()
        }
      }
    },
  })
}
